import classNames from "classnames"
import type {
  CSSProperties,
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import {
  forwardRef,
  useMemo,
} from "react"
import styles from "./Slider.module.scss"

export interface Props extends ComponentPropsWithoutRef<"div"> {
  min: number;
  max: number;
  lowValue: number;
  highValue: number;
  highlight: boolean;
}

/*
 * this element draws a horizontal line behind the slider
 */
export const SliderTrack = forwardRef<HTMLDivElement, Props>(
  function SliderTrack(props: Props, propRef: Ref<HTMLDivElement>) {
    const {
      min,
      max,
      highlight,
      lowValue = min,
      highValue = max,
      ...htmlDivProps
    } = props

    const className = classNames(
      props.className,
      styles.sliderTrack,
      {
        [styles.sliderHighlight]: highlight,
      },
    )

    const style: CSSProperties = useMemo(
      () => {
        const range = max - min

        const percentageOne = Math.min(
          100,
          Math.max(
            0,
            100 * (lowValue - min) / range,
          ),
        )

        const percentageTwo = Math.min(
          100,
          Math.max(
            0,
            100 * (highValue - min) / range,
          ),
        )

        let left = `${percentageOne}%`
        let right = `${100 - percentageTwo}%`

        if (highlight && percentageOne === 0) {
          left = "0.5px"
        }

        if (highlight && percentageTwo === 100) {
          right = "0.5px"
        }

        return {
          ...htmlDivProps.style,
          left,
          right,
        }
      },
      [
        max,
        min,
        lowValue,
        highValue,
        htmlDivProps.style,
      ],
    )

    return (
      <>
        <div
          {...htmlDivProps}
          className={className}
          ref={propRef}
          style={style}
        />
      </>
    )
  },
)
